// The search: the latest games of the last 30 days (the game store's)
// through a named position, examined lazily.
// A game is examined once ever: its path is computed from its moves and the
// book, stored in its folder and summarised in the index. Later searches
// read the index. Scores come from stored scans and are absent otherwise.
import type { Chess } from '@chess/api-chess';
import type { Games } from '@chess/api-games';
import type { Openings } from '@chess/api-openings';
import type { Analysis } from '@chess/api-analysis';
import type { Storage } from '@chess/api-storage';
import type { Log } from '@chess/api-log';
import { FOLDER, readIndex, readPath, writeIndex, writePath } from './files.ts';
import { entryOf, passesThrough, pathOf, type IndexEntry, type OpeningIndex } from './paths.ts';
import { goPlyOf, openingScore, sortRows, type Row } from './score.ts';
import type { GameStore, GameSummary } from '@chess/api-model';

export interface Deps {
  /** The games searched: the last 30 days. */
  store: GameStore;
  /** For each game's folder, where its path is kept. */
  games: Pick<Games, 'folder'>;
  chess: Chess;
  openings: Openings;
  /** Optional: a build without the engine has no scores. */
  analysis?: Analysis;
  /** The plugin's own storage, for the index. */
  storage: Storage;
  log?: Log;
}

export interface SearchResult {
  /** How many of the latest games were examined. */
  searched: number;
  /** How many games the period holds. */
  total: number;
  rows: Row[];
}

export interface OpeningHistory {
  /** The latest `size` games that passed through the position, worst opening score first. */
  search(epd: string, size: number, currentGameId?: string): Promise<SearchResult>;
  /** The row for one game again, after its scan completed. */
  row(entry: IndexEntry, currentGameId?: string): Promise<Row | undefined>;
  /** The index entry of an examined game, if any. */
  entry(gameId: string): IndexEntry | undefined;
}

export function createOpeningHistory(deps: Deps): OpeningHistory {
  let index: OpeningIndex | undefined;

  const loadIndex = async () => (index ??= await readIndex(deps.storage));

  /** The entry for a game, computing and storing it when missing. */
  const examine = async (summary: GameSummary): Promise<IndexEntry | undefined> => {
    const idx = await loadIndex();
    const known = idx.entries[summary.id];
    if (known) return known;
    const folder = deps.games.folder(summary.id, FOLDER);
    let path = await readPath(folder);
    if (!path) {
      const record = await deps.store.record(summary.id);
      if (!record) return undefined;
      let positions;
      let firstMove;
      try {
        const parsed = deps.chess.parsePgn(record.pgn);
        positions = parsed.positions;
        firstMove = parsed.moves[0]?.san;
      } catch (error) {
        deps.log?.warn('game could not be replayed', { gameId: summary.id, error: String(error) });
        return undefined;
      }
      const book = await deps.openings.load(firstMove ?? 'e4');
      path = pathOf(summary.id, positions, book);
      await writePath(folder, path);
    }
    const entry = entryOf(summary, path);
    idx.entries[summary.id] = entry;
    return entry;
  };

  const rowOf = async (entry: IndexEntry, currentGameId?: string): Promise<Row | undefined> => {
    if (!entry.reader) return undefined;
    const scan = deps.analysis ? await deps.analysis.get(entry.gameId).catch(() => undefined) : undefined;
    const running = deps.analysis?.status(entry.gameId)?.running;
    const opening = openingScore(scan, entry.exitPly, entry.moves, entry.reader);
    const score: Row['score'] = opening ? { kind: 'score', score: opening } : running ? { kind: 'scanning' } : { kind: 'unscanned' };
    return {
      gameId: entry.gameId,
      playedAt: entry.playedAt,
      reader: entry.reader,
      result: entry.result,
      exitPly: entry.exitPly,
      score,
      goPly: goPlyOf(opening, entry.exitPly),
      current: entry.gameId === currentGameId,
    };
  };

  return {
    async search(epd, size, currentGameId) {
      await deps.store.ready();
      const latest = deps.store.list({ order: 'newest', size });
      const total = deps.store.count();
      const entries: IndexEntry[] = [];
      let added = 0;
      for (const summary of latest) {
        const before = index?.entries[summary.id];
        const entry = await examine(summary);
        if (entry && !before) added++;
        if (entry) entries.push(entry);
      }
      if (added > 0 && index) await writeIndex(deps.storage, index);
      deps.log?.debug('opening history searched', { epd, searched: latest.length, examined: added });
      const matching = entries.filter((e) => passesThrough(e, epd));
      const rows = (await Promise.all(matching.map((e) => rowOf(e, currentGameId)))).filter((r): r is Row => !!r);
      return { searched: latest.length, total, rows: sortRows(rows) };
    },
    row: rowOf,
    entry: (gameId) => index?.entries[gameId],
  };
}
